<template>
  <v-container class="py-8 px-4" fluid>
    <!-- detail view -->
    <AutomationDetail
      v-if="openAutomation"
      :automation="openAutomation"
      :agents="agents"
      :workflows="workflowChipItems"
      :graph="openWorkflowGraph"
      :run-state="openRunState"
      :running="runInFlight"
      @update:automation="onUpdate"
      @back="openId = null"
      @add-helper="onAddHelper"
      @save="onSave"
      @run="onRun"
      @delete="onDelete"
      @cancel-run="onCancelRun"
      @pause-run="onPauseRun"
      @resume-run="onResumeRun"
    />

    <!-- list view -->
    <template v-else>
      <h1 class="text-h4 font-weight-bold mb-2">Automations</h1>
      <p class="text-body-1 text-medium-emphasis mb-8" style="max-width: 580px">
        Work that runs for you — a daily briefing, a weekly report, an inbox that sorts itself.
        Each one is plain-English here — edit it, run it and watch progress without leaving the page.
      </p>

      <v-row>
        <!-- new -->
        <v-col cols="12" md="6" lg="4">
          <v-card
            variant="outlined"
            rounded="lg"
            height="100%"
            min-height="168"
            class="pa-6 d-flex flex-column justify-center ga-2 ms-add"
            @click="picker = true"
          >
            <v-avatar color="primary" variant="tonal" rounded="lg" size="44">
              <v-icon icon="mdi-plus" />
            </v-avatar>
            <span class="text-subtitle-1 font-weight-bold">New automation</span>
            <span class="text-caption text-disabled">Describe it — I’ll build it</span>
          </v-card>
        </v-col>

        <!-- cards -->
        <v-col v-for="a in list" :key="a.id" cols="12" md="6" lg="4">
          <AutomationCard :automation="a" @open="openId = $event" />
        </v-col>
      </v-row>
    </template>

    <AutomationKindDialog v-model="picker" @pick="onPickKind" />
  </v-container>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import AutomationCard from '@/components/agentpages/automations/AutomationCard.vue'
import AutomationDetail from '@/components/agentpages/automations/AutomationDetail.vue'
import AutomationKindDialog from '@/components/agentpages/automations/AutomationKindDialog.vue'
import { AUTO_OPENERS, addHelperOpener } from '@/data/automations'
import {
  automationsFromStores,
  scheduledRunPayloadFromAutomation,
  workflowConfigFromAutomation,
  factoryConfigFromAutomation,
  factorySchedulePayloadFromAutomation,
  parseWorkflowHelpers
} from '@/utils/automations'
import { buildWorkflowGraph } from '@/utils/workflowGraph'
import { rewriteWorkflowHelperTokens, type HelperTokenEdit } from '@/utils/workflowHelpers'
import { findAgentItemBySelector, agentTitleForSelector } from '@/utils/agentRefs'
import { useWorkflowStream, type WorkflowStreamState } from '@/composables/useWorkflowStream'
import { useFactoryStream, type FactoryStreamState } from '@/composables/useFactoryStream'
import type { Node, Edge } from '@vue-flow/core'
import type { Automation, AutomationType, AutoHelper } from '@/types/automations'
import type { WorkflowConfig } from '@/types/workflows'
import type { FactoryConfig, FactoryScheduleRecord } from '@/types/factories'
import type { ScheduledRun } from '@/types/schedules'
import type { AccessibleAgentItem } from '@/store/agents'
import { getDashboardTabPath } from '@/router/dashboardTabs'

@Options({
  components: {
    AutomationCard,
    AutomationDetail,
    AutomationKindDialog
  },
  computed: {
    ...mapGetters(['workflowList', 'factoryList', 'scheduledRunsList', 'factorySchedulesByFactory', 'agentNames', 'accessibleAgentItems', 'configAgentSlug', 'selectedTab'])
  },
  watch: {
    // The tab stays mounted inside the dashboard v-window, so mounted() runs only once. Refetch when
    // the user (re)enters Automations so a workflow/factory/schedule just created elsewhere (e.g. via
    // the Chat config agent) shows up without a reload.
    selectedTab(value: string) {
      if (value === 'automations') {
        void (this as AutomationsView).refresh()
      }
    },
    // Mirror the Advanced editors' mount behavior: when a workflow/factory detail opens, pick up any
    // in-flight run for it (and drop stream state left over from a previously-open automation).
    openId() {
      void (this as AutomationsView).resumeOpenRun()
    }
  }
})
export default class AutomationsView extends Vue {
  workflowList!: WorkflowConfig[]
  factoryList!: FactoryConfig[]
  scheduledRunsList!: ScheduledRun[]
  factorySchedulesByFactory!: (factoryId: string) => FactoryScheduleRecord[]
  agentNames!: string[]
  accessibleAgentItems!: AccessibleAgentItem[]
  configAgentSlug!: string | null
  selectedTab!: string

  openId: string | null = null
  picker = false

  // In-session edits made through the abstract editor; persisted by the detail's autosave (onSave
  // folds them back onto the real WorkflowConfig / FactoryConfig / ScheduledRun via the write-backs).
  localEdits: Record<string, Automation> = {}

  // Bumped per automation on every edit. Autosave runs in the background while the user keeps
  // editing, so onSave only clears localEdits when nothing changed during the round-trip — otherwise
  // the store echo would clobber keystrokes typed while the save was in flight.
  private editSeqs: Record<string, number> = {}

  // The same streaming composables the Advanced editors use — one per kind, resumed per open record.
  private workflowStream: ReturnType<typeof useWorkflowStream> | null = null
  private factoryStream: ReturnType<typeof useFactoryStream> | null = null
  runInFlight = false

  created() {
    this.workflowStream = useWorkflowStream()
    this.factoryStream = useFactoryStream(this.$store)
  }

  async mounted() {
    await this.refresh()
  }

  beforeUnmount() {
    // Close any live SSE subscription on route leave/logout (tab switches keep the
    // view mounted in the v-window; resumeOpenRun re-attaches on detail open).
    this.workflowStream?.reset()
    this.factoryStream?.reset()
  }

  async refresh() {
    await Promise.all([
      // Automations is the simple-mode lens over Advanced Workflows, Factories and Schedules. Those
      // records also render against the current agent catalog, so refresh owned/shared agents with
      // the automation lists whenever this tab is opened.
      this.$store.dispatch('fetchModelConfigs'),
      this.$store.dispatch('fetchPublishedAgents'),
      this.$store.dispatch('fetchSharedAgents'),
      this.$store.dispatch('fetchWorkflows'),
      this.$store.dispatch('fetchFactories'),
      this.$store.dispatch('fetchFactorySchedules'),
      this.$store.dispatch('fetchScheduledRuns')
    ])
  }

  get agents(): string[] {
    return this.agentNames
  }

  // The workflows a factory step can run — same source list the Advanced FactoryEditor's workflow
  // select uses (workflowList), reduced to what the step chips need.
  get workflowChipItems(): Array<{ id: string; name: string }> {
    return (this.workflowList || []).map((w) => ({ id: w.id, name: w.name }))
  }

  // The list is a read-only lens over the real stores (see src/utils/automations.ts).
  get storeAutomations(): Automation[] {
    return automationsFromStores({
      workflows: this.workflowList || [],
      factories: this.factoryList || [],
      scheduledRuns: this.scheduledRunsList || [],
      agents: this.accessibleAgentItems || [],
      factorySchedules: this.factorySchedulesByFactory
    })
  }

  get list(): Automation[] {
    // Surface every automation the lens knows about — workflows, factories and schedules. In simple
    // mode there are no dedicated Workflows/Factories tabs, so this list is the only place a
    // newly-created workflow or factory appears.
    return this.storeAutomations.map((a) => this.localEdits[a.id] ?? a)
  }

  get openAutomation(): Automation | undefined {
    return this.list.find((a) => a.id === this.openId)
  }

  // The visual graph for the open workflow — the same nodes/edges the Advanced Workflows tab renders.
  // Keyed off the persisted config (openId + workflowList), NOT openAutomation/localEdits: per-keystroke
  // edits would otherwise invalidate the computed cache and re-run dagre layout on every input event.
  get openWorkflowGraph(): { nodes: Node[]; edges: Edge[] } | null {
    if (!this.openId) return null
    const workflow = (this.workflowList || []).find((w) => w.id === this.openId)
    if (!workflow) return null
    return buildWorkflowGraph(workflow, this.accessibleAgentItems || [])
  }

  // The live stream state for the open automation's kind, fed to the embedded Advanced run browsers.
  get openRunState(): WorkflowStreamState | FactoryStreamState | null {
    const open = this.openAutomation
    if (open?.type === 'workflow') return this.workflowStream?.state ?? null
    if (open?.type === 'factory') return this.factoryStream?.state ?? null
    return null
  }

  // Mirrors the Advanced editors on mount/id-change: reset stale state, then attach to any active run.
  // BOTH streams reset unconditionally — leaving the detail (or switching kinds) would otherwise leave
  // the previous automation's SSE subscription streaming into state nothing renders.
  async resumeOpenRun() {
    this.workflowStream?.reset()
    this.factoryStream?.reset()
    const open = this.openAutomation
    if (open?.type === 'workflow' && this.workflowStream) {
      await this.workflowStream.resume(open.id, { onlyActive: true })
    } else if (open?.type === 'factory' && this.factoryStream) {
      await this.factoryStream.resume(open.id, { onlyActive: true })
    }
  }

  onUpdate(next: Automation, helperEdit?: HelperTokenEdit) {
    // Workflow helper chips and the goal text are two views of the same mainPrompt. A structural chip
    // change arrives as an explicit HelperTokenEdit from the detail view (the chip UI reports exactly
    // what the user did — intent is never inferred by diffing chip arrays; a misfired diff once
    // deleted real helper tokens). Fold it into `job` as a token rewrite, then re-derive the chips
    // from the rewritten prompt so they stay deduped and aligned with the tokens (an edit can merge
    // two pairs into one). Roles are session-only display text with no home in the prompt — carried
    // over by pair.
    if (next.type === 'workflow' && helperEdit) {
      const source = (this.workflowList || []).find((w) => w.id === next.id)
      const agents = this.accessibleAgentItems || []
      const job = rewriteWorkflowHelperTokens(next.job, source?.delimiter, [helperEdit], agents)
      const roleByPair = new Map((next.helpers || []).map((h) => [`${h.agent}::${h.input}`, h.role]))
      const helpers = parseWorkflowHelpers(job, source?.delimiter).map((h) => {
        const agent = agentTitleForSelector(h.agent, agents)
        return { ...h, agent, role: roleByPair.get(`${agent}::${h.input}`) || h.role }
      })
      next = { ...next, job, helpers }
    }
    this.localEdits = { ...this.localEdits, [next.id]: next }
    this.editSeqs[next.id] = (this.editSeqs[next.id] || 0) + 1
  }

  private clearLocalEdit(id: string) {
    const { [id]: _discard, ...rest } = this.localEdits
    this.localEdits = rest
  }

  // Persist a lens edit through the same store actions the Advanced editors save with. All three
  // actions return false on API failure — local edits are kept in that case so the user can retry
  // instead of silently losing them. No refetch needed on success: saveWorkflow/saveFactory commit
  // the backend-normalized record into the store themselves.
  async onSave(automation: Automation): Promise<boolean> {
    const seqAtStart = this.editSeqs[automation.id] || 0
    const agents = this.accessibleAgentItems || []
    let ok = false
    if (automation.type === 'schedule') {
      const source = (this.scheduledRunsList || []).find((r) => r.id === automation.id)
      if (!source) return false
      const payload = scheduledRunPayloadFromAutomation(automation, source)
      ok = !!(await this.$store.dispatch('updateScheduledRun', { id: automation.id, payload }))
    } else if (automation.type === 'workflow') {
      const source = (this.workflowList || []).find((w) => w.id === automation.id)
      if (!source) return false
      ok = !!(await this.$store.dispatch('saveWorkflow', workflowConfigFromAutomation(automation, source, agents)))
    } else {
      const source = (this.factoryList || []).find((f) => f.id === automation.id)
      if (!source) return false
      ok = !!(await this.$store.dispatch('saveFactory', factoryConfigFromAutomation(automation, source, agents)))
      // The factory's schedule lives in its own record. "On a schedule" creates/updates + enables
      // the lens-managed (first) one; any other mode disables it — never deletes, so details owned
      // by Advanced (extra times, payload, label) survive a mode round-trip. Running records are
      // left alone, matching the Advanced card's mid-run guard.
      if (ok) {
        const schedule = (this.factorySchedulesByFactory?.(automation.id) || [])[0]
        if (automation.when.mode === 'schedule') {
          ok = !!(await this.$store.dispatch('saveFactorySchedule', factorySchedulePayloadFromAutomation(automation, schedule)))
        } else if (schedule && schedule.status === 'enabled') {
          ok = !!(await this.$store.dispatch('saveFactorySchedule', { ...schedule, status: 'disabled' as const }))
        }
      }
    }
    if (!ok) return false
    // Edits arrived while this save was in flight: keep the newer localEdits (their own debounced
    // save will persist them). Clearing here would echo the just-saved (older) state into the editor.
    if ((this.editSeqs[automation.id] || 0) !== seqAtStart) return true
    this.clearLocalEdit(automation.id)
    // Factory inputs are run-time carry payload with no home on FactoryConfig — keep them in session
    // after a save so they survive to feed the next Run instead of visibly vanishing.
    if (automation.type === 'factory' && (automation.inputs || []).length) {
      const fresh = this.storeAutomations.find((a) => a.id === automation.id)
      if (fresh) this.localEdits = { ...this.localEdits, [automation.id]: { ...fresh, inputs: automation.inputs } }
    }
    return true
  }

  // Run = the Advanced sequence exactly (WorkflowEditor.run / FactoryEditor.runFactory): validate,
  // persist the config, then execute via the stream; progress lands in the embedded run browser.
  // A failed save aborts the run — executing would silently run the stale persisted config.
  async onRun(automation: Automation) {
    const agents = this.accessibleAgentItems || []
    this.runInFlight = true
    try {
      if (automation.type === 'workflow') {
        const source = (this.workflowList || []).find((w) => w.id === automation.id)
        if (!source || !this.workflowStream) return
        const config = workflowConfigFromAutomation(automation, source, agents)
        if (!config.mainAgentRef || !findAgentItemBySelector(config.mainAgentRef, agents)) {
          this.$store.dispatch('updateSnackbar', { text: 'Lead assistant must be selected.', color: 'error' }, { root: true })
          return
        }
        try {
          JSON.parse(config.dataPayload || '{}')
        } catch {
          this.$store.dispatch('updateSnackbar', { text: 'Invalid JSON in data payload.', color: 'error' }, { root: true })
          return
        }
        if (!(await this.onSave(automation))) return
        await this.workflowStream.execute(automation.id)
      } else if (automation.type === 'factory') {
        if (!this.factoryStream) return
        if (!(await this.onSave(automation))) return
        // The factory's "What it works with" free-text payload is handed straight to the run as its
        // initial carry payload — the same raw string the Advanced editor's "Initial Payload" field
        // sends (default 'execute'). Session-only; defaulted in case an older edit lacks it.
        await this.factoryStream.execute(automation.id, automation.carryPayload ?? 'execute')
      }
    } catch (e) {
      this.$store.dispatch(
        'updateSnackbar',
        { text: (e as Error)?.message || 'An unexpected error occurred.', color: 'error' },
        { root: true }
      )
    } finally {
      this.runInFlight = false
    }
  }

  // Delete the real record through the same store actions Advanced deletes with (the actions show
  // their own error snackbars). On success drop the local edit state and return to the list.
  async onDelete(automation: Automation) {
    let ok = false
    if (automation.type === 'workflow') {
      ok = !!(await this.$store.dispatch('deleteWorkflow', automation.id))
    } else if (automation.type === 'factory') {
      ok = !!(await this.$store.dispatch('deleteFactory', automation.id))
    } else {
      // deleteScheduledRun returns undefined on success AND failure — success means the row left the store.
      await this.$store.dispatch('deleteScheduledRun', automation.id)
      ok = !(this.scheduledRunsList || []).some((r) => r.id === automation.id)
    }
    if (!ok) return
    this.clearLocalEdit(automation.id)
    delete this.editSeqs[automation.id]
    this.openId = null
  }

  onCancelRun(automation: Automation) {
    if (automation.type === 'workflow') void this.workflowStream?.cancel()
    else if (automation.type === 'factory') void this.factoryStream?.cancel()
  }

  onPauseRun() {
    void this.factoryStream?.pause()
  }

  onResumeRun() {
    void this.factoryStream?.resumeRun()
  }

  // Open the Configuration Assistant with a seeded message. When it's published as a chat agent we
  // jump to the Chat tab and auto-send; otherwise we fall back to the config-chat dialog.
  openConfigChat(message: string) {
    const slug = this.configAgentSlug
    if (slug) {
      this.$store.dispatch('setPendingChatMessage', message)
      this.$store.dispatch('setPendingChatAgent', { slug })
      this.$store.dispatch('setTab', 'chat')
      const path = getDashboardTabPath('chat')
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
    } else {
      this.$store.dispatch('setConfigChatSeed', message)
      this.$store.dispatch('toggleConfigChat', true)
    }
  }

  // Hand off to the Configuration Assistant for the chosen kind.
  onPickKind(kind: AutomationType) {
    this.picker = false
    this.openConfigChat(AUTO_OPENERS[kind])
  }

  // "Add a helper" hands the user to the Configuration Assistant chat: a NEW helper needs an agent and
  // an interpolation design, which the chat agent works out conversationally. (Edits/removals of
  // existing helpers are handled locally — onUpdate folds them into the prompt as token rewrites.)
  onAddHelper(automation: Automation) {
    this.openConfigChat(addHelperOpener(automation.name))
  }

}
</script>

<style scoped lang="scss">
.ms-add {
  cursor: pointer;
  border-style: dashed;
  transition: border-color 0.16s;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }
}

</style>
